// src/pages/UserPanel/components/AvatarModal.jsx

import React, { useState, useRef } from "react";
import { toast } from 'react-toastify';
import AvatarWithInitials from '../../components/AvatarWithInitials';
import { API_URL } from '../../services/api/config';

export default function AvatarModal({
  currentAvatar,
  avatarsDisponibles,
  usuario, // Objeto usuario completo
  onClose,
  onSaveAvatar,
}) {
  const [tempAvatar, setTempAvatar] = useState(currentAvatar);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileInputRef = useRef(null);

  function handleSave() {
    onSaveAvatar(tempAvatar);
  }

  // Abrir selector de archivos
  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  // Manejar seleccion de archivo
  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    // Validar tamano (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    // Mostrar preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(ev.target.result);
    };
    reader.readAsDataURL(file);

    // Subir imagen
    setUploading(true);
    try {
      const token = localStorage.getItem('nfcToken');
      const formData = new FormData();
      formData.append('foto', file);

      const response = await fetch(`${API_URL}/api/usuarios/avatar/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al subir la imagen');
      }

      const data = await response.json();
      const uploadedUrl = data.data?.url || data.url;

      if (uploadedUrl) {
        setTempAvatar(uploadedUrl);
        toast.success('Foto subida correctamente');
      } else {
        throw new Error('No se recibio URL de la imagen');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(error.message || 'Error al subir la foto');
      setUploadPreview(null);
    } finally {
      setUploading(false);
      // Limpiar input para permitir subir el mismo archivo de nuevo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  // Determinar si es una foto subida (no un avatar predefinido ni iniciales)
  const isCustomPhoto = tempAvatar && !avatarsDisponibles.includes(tempAvatar) && tempAvatar !== null;

  return (
    <div
      className="modal show"
      style={{
        display: "flex",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        background: "rgba(0,0,0,0.5)",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto"
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ margin: 0 }}>
        <div className="modal-content"
             style={{borderRadius: 'var(--radius-xl)'}}>

          {/* Header con gradiente feature */}
          <div className="modal-header modal-header-gradient">
            <h5 className="modal-title"
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--white)'
                }}>
              <i className="bi bi-person-circle me-2"></i>
              Personalizar Avatar
            </h5>
            <button type="button"
                    className="btn-close btn-close-white"
                    onClick={onClose}
                    disabled={uploading} />
          </div>

          {/* Body */}
          <div className="modal-body"
               style={{padding: 'var(--space-5)'}}>

            {/* Vista previa del avatar actual */}
            <div className="mb-4 p-3"
                 style={{
                   backgroundColor: 'var(--gray-100)',
                   borderRadius: 'var(--radius-lg)',
                   border: '1px solid var(--gray-300)'
                 }}>
              <p className="text-muted mb-3"
                 style={{fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)'}}>
                <i className="bi bi-eye me-1"></i>
                Vista previa actual:
              </p>
              <div className="d-flex align-items-center justify-content-center">
                <AvatarWithInitials
                  usuario={{
                    ...usuario,
                    urlAvatar: uploadPreview || tempAvatar // Mostrar preview o avatar temporal
                  }}
                  size={80}
                />
              </div>
            </div>

            <p className="text-muted mb-3"
               style={{fontSize: 'var(--font-size-sm)'}}>
              <i className="bi bi-hand-index me-1"></i>
              Elige como quieres que aparezca tu perfil:
            </p>

            {/* Boton especial para usar iniciales */}
            <div className="mb-4">
              <button
                className={`w-100 d-flex align-items-center justify-content-between p-3 ${tempAvatar === null ? 'btn-feature' : ''}`}
                onClick={() => {
                  setTempAvatar(null);
                  setUploadPreview(null);
                }}
                disabled={uploading}
                style={{
                  backgroundColor: tempAvatar === null ? 'var(--feature-primary)' : 'var(--white)',
                  color: tempAvatar === null ? 'var(--white)' : 'var(--gray-700)',
                  border: tempAvatar === null ? 'none' : '2px solid var(--gray-300)',
                  borderRadius: 'var(--radius-lg)',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-base)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <AvatarWithInitials
                      usuario={{
                        ...usuario,
                        urlAvatar: null // Siempre mostrar iniciales aqui
                      }}
                      size={50}
                    />
                  </div>
                  <div className="text-start">
                    <div style={{fontWeight: 'var(--font-weight-bold)'}}>
                      Usar mis iniciales
                    </div>
                    <div style={{
                      fontSize: 'var(--font-size-xs)',
                      opacity: 0.8,
                      marginTop: '2px'
                    }}>
                      Personalizado con tu nombre
                    </div>
                  </div>
                </div>
                {tempAvatar === null && (
                  <i className="bi bi-check-circle-fill"
                     style={{fontSize: 'var(--font-size-xl)'}}></i>
                )}
              </button>
            </div>

            {/* Divisor */}
            <div className="d-flex align-items-center my-4">
              <div style={{flex: 1, height: '1px', backgroundColor: 'var(--gray-300)'}}></div>
              <span className="px-3 text-muted" style={{fontSize: 'var(--font-size-xs)'}}>
                O elige un avatar
              </span>
              <div style={{flex: 1, height: '1px', backgroundColor: 'var(--gray-300)'}}></div>
            </div>

            {/* Grid de avatares */}
            <div className="d-flex flex-wrap justify-content-center"
                 style={{gap: 'var(--space-3)'}}>
              {avatarsDisponibles.map((avatar, index) => {
                const isSelected = tempAvatar === avatar;

                return (
                  <div
                    key={avatar}
                    className="text-center transition-base"
                    style={{
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      opacity: uploading ? 0.6 : 1
                    }}
                    onClick={() => {
                      if (!uploading) {
                        setTempAvatar(avatar);
                        setUploadPreview(null);
                      }
                    }}
                    title={`Avatar ${index + 1}`}
                  >
                    <div style={{position: 'relative'}}>
                      <img
                        src={avatar}
                        alt={`Avatar ${index + 1}`}
                        className={isSelected ? 'avatar-feature' : ''}
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: 'var(--radius-full)',
                          border: isSelected
                            ? `3px solid var(--feature-primary)`
                            : `2px solid var(--gray-300)`,
                          transition: 'var(--transition-base)',
                          boxShadow: isSelected
                            ? 'var(--shadow-md)'
                            : 'var(--shadow-sm)',
                        }}
                      />

                      {/* Checkmark para el seleccionado */}
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          bottom: '-5px',
                          right: '-5px',
                          backgroundColor: 'var(--feature-primary)',
                          borderRadius: 'var(--radius-full)',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `2px solid var(--white)`,
                          boxShadow: 'var(--shadow-base)'
                        }}>
                          <i className="bi bi-check-lg text-white"
                             style={{fontSize: 'var(--font-size-sm)'}}></i>
                        </div>
                      )}
                    </div>

                    {/* Label debajo del avatar */}
                    <div style={{
                      fontSize: 'var(--font-size-xs)',
                      color: isSelected ? 'var(--feature-primary)' : 'var(--gray-600)',
                      fontWeight: isSelected ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
                      marginTop: 'var(--space-2)'
                    }}>
                      Avatar {index + 1}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Divisor para subir foto */}
            <div className="d-flex align-items-center my-4">
              <div style={{flex: 1, height: '1px', backgroundColor: 'var(--gray-300)'}}></div>
              <span className="px-3 text-muted" style={{fontSize: 'var(--font-size-xs)'}}>
                O sube tu foto
              </span>
              <div style={{flex: 1, height: '1px', backgroundColor: 'var(--gray-300)'}}></div>
            </div>

            {/* Boton para subir foto propia */}
            <div className="mb-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                className={`w-100 d-flex align-items-center justify-content-between p-3 ${isCustomPhoto ? 'btn-feature' : ''}`}
                onClick={handleUploadClick}
                disabled={uploading}
                style={{
                  backgroundColor: isCustomPhoto ? 'var(--feature-primary)' : 'var(--white)',
                  color: isCustomPhoto ? 'var(--white)' : 'var(--gray-700)',
                  border: isCustomPhoto ? 'none' : '2px solid var(--gray-300)',
                  borderRadius: 'var(--radius-lg)',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-base)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  opacity: uploading ? 0.8 : 1
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: 'var(--radius-full)',
                      backgroundColor: isCustomPhoto ? 'rgba(255,255,255,0.2)' : 'var(--gray-100)',
                      border: isCustomPhoto ? 'none' : '2px dashed var(--gray-400)'
                    }}
                  >
                    {uploading ? (
                      <span className="spinner-border spinner-border-sm"
                            style={{color: isCustomPhoto ? 'var(--white)' : 'var(--gray-500)'}}></span>
                    ) : (
                      <i className="bi bi-camera-fill"
                         style={{
                           fontSize: 'var(--font-size-xl)',
                           color: isCustomPhoto ? 'var(--white)' : 'var(--gray-500)'
                         }}></i>
                    )}
                  </div>
                  <div className="text-start">
                    <div style={{fontWeight: 'var(--font-weight-bold)'}}>
                      {uploading ? 'Subiendo...' : 'Subir mi foto'}
                    </div>
                    <div style={{
                      fontSize: 'var(--font-size-xs)',
                      opacity: 0.8,
                      marginTop: '2px'
                    }}>
                      {isCustomPhoto ? 'Foto personalizada activa' : 'Imagen JPG o PNG (max 5MB)'}
                    </div>
                  </div>
                </div>
                {isCustomPhoto && !uploading && (
                  <i className="bi bi-check-circle-fill"
                     style={{fontSize: 'var(--font-size-xl)'}}></i>
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer"
               style={{
                 padding: 'var(--space-4) var(--space-5)',
                 borderTop: `1px solid var(--gray-300)`
               }}>

            {/* Boton Cancelar */}
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={uploading}>
              <i className="bi bi-x-circle me-1"></i>
              Cancelar
            </button>

            {/* Boton Guardar */}
            <button
              className="btn btn-success"
              onClick={handleSave}
              disabled={uploading}>
              <i className="bi bi-check-circle me-1"></i>
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
